/* eslint-disable no-shadow */
import clsx from 'clsx';
import React, { ReactNode, useEffect, useState } from 'react';
import Tabs from 'rc-tabs';
import SwatchFilter from '../SwatchFilter';

import { ITab, ITabs } from '../../types/customizer';

type IProps = {
  tabs: ITabs;
  tabStyle?: 'single' | 'box' | 'underline';
  onChange: (updated: any) => void;
  isFetching: boolean;
  activeKey: string;
  onChangeParentActiveKey?: (key: string) => void;
  innerTab?: number;
  pathPrefix?: string;
  navigateToUrl?: (url: string) => void;
};

const AppTabs = ({
  tabs,
  tabStyle = 'underline',
  onChange,
  isFetching,
  activeKey,
  onChangeParentActiveKey,
  innerTab = 1,
  pathPrefix,
  navigateToUrl
}: IProps) => {
  const [keyList, setKeyList] = useState<string[]>([]);
  const [key, setKey] = useState<string>(activeKey);

  useEffect(() => {
    const list = tabs.map((t) => t.eventKey);
    setKeyList(list);
  }, [tabs]);

  useEffect(() => {
    if (!keyList.includes(key)) {
      setKey(keyList[0]);
    }
  }, [keyList, key]);

  useEffect(() => {
    setKey(activeKey);
  }, [activeKey]);

  const isInitial = () => {
    const currentIndex = keyList.indexOf(key);
    return currentIndex === 0;
  };

  const isLast = () => {
    const currentIndex = keyList.indexOf(key);
    return currentIndex === keyList.length - 1;
  };

  const handlePrev = () => {
    const currentIndex = keyList.indexOf(key);
    let index = currentIndex - 1;
    if (index < 0) {
      index = keyList.length - 1;
    }
    setKey(keyList[index]);
    if (onChangeParentActiveKey) onChangeParentActiveKey(keyList[index]);
  };

  const handleNext = () => {
    const currentIndex = keyList.indexOf(key);
    let index = currentIndex + 1;
    if (index >= keyList.length) {
      index = 0;
    }
    setKey(keyList[index]);
    if (onChangeParentActiveKey) onChangeParentActiveKey(keyList[index]);
  };

  const handleTabClick = (selectedOpt: string) => {
    if (keyList.length) {
      const currentIndex = keyList.indexOf(selectedOpt);
      const currentTab = tabs[currentIndex];
      if (currentTab?.actionPayload) {
        onChange(currentTab?.actionPayload);
      }
    }
  };

  const getTabTitle = (tab: ITab, index: number) => {
    const isActive = tab.eventKey === key;
    const tabTitle = tab.title;
    let title: string | ReactNode = '';
    if (typeof tabTitle === 'string') {
      if (innerTab === 1) {
        title = tabTitle;
      } else {
        title = `${keyList.includes('none') || keyList.includes('engraving_none') ? index : index + 1}. ${tabTitle}`;
      }
    } else {
      title = tabTitle(isActive);
    }
    return (
      <>
        <div
          className={clsx({
            'rc-single-tab tw-flex tw-justify-between lg:tw-hidden tw-items-center': tabStyle === 'single',
            'tw-hidden': tabStyle === 'single' && !isActive,
            'rc-box rc-box-tab-case': tabStyle === 'box',
            'rc-box-active': tabStyle === 'box' && isActive,
            'rc-underline': tabStyle === 'underline',
            'rc-underline-active': tabStyle === 'underline' && isActive
          })}
        >
          {tabStyle === 'single' && (
            <i
              className={clsx('fa fa-chevron-left tw-text-brand-color tw-cursor-pointer tw-p-3 -tw-m-3', {
                invisible: isInitial()
              })}
              onClick={handlePrev}
              role="presentation"
            />
          )}
          <span className="tw-flex tw-justify-center tw-min-w-[20px] tw-text-primary-color">{title}</span>
          {tabStyle === 'single' && (
            <i
              className={clsx('fa fa-chevron-right tw-text-brand-color tw-cursor-pointer tw-p-3 -tw-m-3', {
                invisible: isLast()
              })}
              onClick={handleNext}
              role="presentation"
            />
          )}
        </div>
      </>
    );
  };

  const getActiveTab = (items: ITab[]) => {
    const selectedTab = items.find((tab) => tab.isSelected);
    let key = '';
    if (selectedTab) {
      key = selectedTab.eventKey;
    } else {
      key = items?.[0]?.eventKey || '';
    }
    return key;
  };

  const getTabContent = (tab: any) => {
    const Component = tab.component || SwatchFilter;
    return (
      <Component
        content={tab.content}
        onChange={onChange}
        isFetching={isFetching}
        innerTab={innerTab}
        tabs={tabs}
        pathPrefix={pathPrefix}
        navigateToUrl={navigateToUrl}
        onSelectedClick={innerTab > 1 && keyList.length > 1 ? handleNext : undefined}
      />
    );
  };

  const items = tabs.map((tab: ITab, index: number) => ({
    key: tab.eventKey,
    label: getTabTitle(tab, index),
    children: (
      <>
        {tab.items && tab.items.length > 0 && (
          <AppTabs
            tabs={tab.items}
            tabStyle={tab.tabStyle}
            activeKey={getActiveTab(tab.items)}
            isFetching={isFetching}
            onChange={onChange}
            innerTab={innerTab + 1}
          />
        )}
        {(!tab.items || !tab.items.length) && getTabContent(tab)}
      </>
    )
  }));

  return (
    <Tabs
      activeKey={key}
      onChange={setKey}
      onTabClick={handleTabClick}
      className={clsx('tw-border-hidden', {
        'single-tab-container': tabStyle === 'single'
      })}
      items={items}
    />
  );
};

export default AppTabs;
